import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  BadgeCheck,
  Camera,
  Clock3,
  Loader2,
  Mail,
  Phone,
  Save,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  User,
} from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { apiService } from '../../services/api'
import { User as UserType } from '../../types'

function escapeXml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function buildAvatarDataUrl(name: string) {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('') || 'U'

  const hash = Array.from(name || 'user').reduce((acc, char) => {
    return ((acc << 5) - acc + char.charCodeAt(0)) >>> 0
  }, 0)

  const hue1 = hash % 360
  const hue2 = (hue1 + 48) % 360
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" role="img" aria-label="Avatar">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="hsl(${hue1} 82% 52%)"/>
          <stop offset="100%" stop-color="hsl(${hue2} 78% 46%)"/>
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="25%" r="70%">
          <stop offset="0%" stop-color="rgba(255,255,255,.35)"/>
          <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
        </radialGradient>
      </defs>
      <rect width="256" height="256" rx="128" fill="url(#bg)"/>
      <circle cx="128" cy="104" r="112" fill="url(#glow)"/>
      <text
        x="128"
        y="148"
        text-anchor="middle"
        font-family="Inter, Arial, sans-serif"
        font-size="92"
        font-weight="700"
        fill="white"
      >${escapeXml(initials)}</text>
    </svg>
  `

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatPhone(codeArea: string, area: string, phone: string) {
  const formattedPhone = phone.replace(/(\d{4,5})(\d{4})$/, '$1-$2')
  return `${codeArea} (${area}) ${formattedPhone}`
}

function getAvatarStorageKey(userId: string) {
  return `ambcar.profile.avatar.${userId}`
}

function normalizeDealershipRole(role?: string): UserType['dealershipRole'] {
  if (!role) return undefined

  const normalized = role.toLowerCase()
  const allowedRoles: Array<NonNullable<UserType['dealershipRole']>> = [
    'owner',
    'manager',
    'salesperson',
    'financial',
    'mechanic',
    'viewer',
  ]

  if (normalized === 'seller') return 'salesperson'
  return allowedRoles.includes(normalized as NonNullable<UserType['dealershipRole']>)
    ? (normalized as NonNullable<UserType['dealershipRole']>)
    : undefined
}

type ProfileApiUser = Omit<UserType, 'context' | 'updatedAt' | 'dealershipRole' | 'backofficeRole' | 'permissions'> & {
  context?: UserType['context']
  updatedAt?: string
  dealershipRole?: string
  backofficeRole?: UserType['backofficeRole']
  permissions?: string[]
}

function getRoleLabel(role: string | undefined, t: (key: string) => string) {
  if (!role) return '—'
  const normalized = role.toLowerCase()
  const map: Record<string, string> = {
    owner: t('myProfile.roles.owner'),
    manager: t('myProfile.roles.manager'),
    salesperson: t('myProfile.roles.salesperson'),
    financial: t('myProfile.roles.financial'),
    mechanic: t('myProfile.roles.mechanic'),
    viewer: t('myProfile.roles.viewer'),
  }
  return map[normalized] || role
}

function parsePrimaryPhone(user: UserType | null) {
  const primaryPhone = user?.phones?.find(phone => phone.isPrimary) || user?.phones?.[0]
  const codeArea = primaryPhone?.codeArea || '+55'
  const area = primaryPhone?.area || '11'
  const phone = primaryPhone?.phone || ''
  return { codeArea, area, phone }
}

export const MyProfilePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, updateUser: updateAuthUser } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [profile, setProfile] = useState<UserType | null>(user)
  const [avatarPreview, setAvatarPreview] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [formData, setFormData] = useState({
    name: user?.name || '',
    codeArea: '+55',
    area: '11',
    phone: '',
  })

  const displayUser = profile || user
  const displayAvatar = avatarPreview || displayUser?.avatar || buildAvatarDataUrl(displayUser?.name || 'User')

  useEffect(() => {
    if (!user) return

    let active = true

    const loadProfile = async () => {
      setIsLoadingProfile(true)
      try {
        const currentProfile = await apiService.getUserById('me') as ProfileApiUser
        if (!active) return

        const avatarKey = getAvatarStorageKey(currentProfile.id)
        const storedAvatar = localStorage.getItem(avatarKey)
        const resolvedAvatar = storedAvatar || user.avatar || currentProfile.avatar || ''
        const mergedProfile: UserType = {
          ...currentProfile,
          context: currentProfile.context || user.context,
          updatedAt: currentProfile.updatedAt || user.updatedAt,
          dealershipRole: normalizeDealershipRole(currentProfile.dealershipRole) || user.dealershipRole,
          avatar: resolvedAvatar || undefined,
          phones: currentProfile.phones,
          isActive: currentProfile.isActive ?? user.isActive,
          status: currentProfile.status,
          role: currentProfile.role,
        }

        setProfile(mergedProfile)
        setAvatarPreview(resolvedAvatar)

        const primaryPhone = parsePrimaryPhone(mergedProfile)
        setFormData({
          name: mergedProfile.name || '',
          codeArea: primaryPhone.codeArea,
          area: primaryPhone.area,
          phone: primaryPhone.phone,
        })

        updateAuthUser(mergedProfile)
        if (resolvedAvatar) {
          localStorage.setItem(avatarKey, resolvedAvatar)
        }
      } catch (loadError) {
        console.error('Erro ao carregar perfil:', loadError)
        if (!active) return

        const fallbackUser = user
        const avatarKey = getAvatarStorageKey(fallbackUser.id)
        const storedAvatar = localStorage.getItem(avatarKey)
        const resolvedAvatar = storedAvatar || fallbackUser.avatar || ''

        setProfile(fallbackUser)
        setAvatarPreview(resolvedAvatar)

        const primaryPhone = parsePrimaryPhone(fallbackUser)
        setFormData({
          name: fallbackUser.name || '',
          codeArea: primaryPhone.codeArea,
          area: primaryPhone.area,
          phone: primaryPhone.phone,
        })
      } finally {
        if (active) {
          setIsLoadingProfile(false)
        }
      }
    }

    void loadProfile()

    return () => {
      active = false
    }
  }, [user, updateAuthUser])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    setFormData(prev => {
      if (name === 'codeArea') {
        const cleaned = value.replace(/[^\d+]/g, '')
        return { ...prev, codeArea: cleaned.startsWith('+') ? cleaned : `+${cleaned.replace(/\+/g, '')}` }
      }
      if (name === 'area') {
        return { ...prev, area: value.replace(/\D/g, '').slice(0, 3) }
      }
      if (name === 'phone') {
        return { ...prev, phone: value.replace(/\D/g, '').slice(0, 9) }
      }
      return { ...prev, [name]: value }
    })
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !displayUser) return

    const reader = new FileReader()
    reader.onload = ev => {
      const dataUrl = String(ev.target?.result || '')
      setAvatarPreview(dataUrl)
      updateAuthUser({ avatar: dataUrl })
      localStorage.setItem(getAvatarStorageKey(displayUser.id), dataUrl)
      setProfile(prev => prev ? { ...prev, avatar: dataUrl } : prev)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveAvatar = () => {
    if (!displayUser) return
    setAvatarPreview('')
    updateAuthUser({ avatar: undefined })
    localStorage.removeItem(getAvatarStorageKey(displayUser.id))
    setProfile(prev => prev ? { ...prev, avatar: undefined } : prev)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError(t('myProfile.nameRequired'))
      return
    }

    if (!displayUser) return

    setIsSaving(true)
    setError('')
    setSuccess('')

    try {
      const phoneDigits = formData.phone.replace(/\D/g, '')
      if (!phoneDigits) {
        throw new Error('phone_required')
      }

      const response = await apiService.updateMyProfile({
        name: formData.name.trim(),
        phones: [
          {
            codeArea: formData.codeArea.trim() || '+55',
            area: formData.area.trim() || '11',
            phone: phoneDigits,
            isPrimary: true,
          },
        ],
      })

      const avatarKey = getAvatarStorageKey(displayUser.id)
      const resolvedAvatar = avatarPreview || displayUser.avatar || localStorage.getItem(avatarKey) || ''
      const updatedUser = {
        ...response.user,
        avatar: resolvedAvatar || undefined,
      }

      setProfile(updatedUser)
      updateAuthUser(updatedUser)
      if (resolvedAvatar) {
        localStorage.setItem(avatarKey, resolvedAvatar)
      }

      setSuccess(t('myProfile.success'))
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && err.message === 'phone_required'
        ? t('myProfile.phoneRequired')
        : err instanceof Error
          ? err.message
          : 'Erro ao salvar perfil'
      setError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoadingProfile || !displayUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const primaryPhone = parsePrimaryPhone(displayUser)
  const userStatusLabel = displayUser.isActive ? t('myProfile.statusActive') : t('myProfile.statusInactive')
  const contextLabel = displayUser.context === 'backoffice'
    ? t('myProfile.contextBackoffice')
    : t('myProfile.contextDealership')

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-accent via-primary to-primary-foreground text-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.45)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_35%)]" />
        <div className="relative flex flex-col gap-6 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative mx-auto sm:mx-0">
              <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-white/20 bg-white/10 shadow-2xl">
                {displayAvatar ? (
                  <img
                    src={displayAvatar}
                    alt={displayUser.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User className="h-12 w-12 text-white/80" />
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white text-primary shadow-lg transition-transform hover:scale-105"
                aria-label={t('myProfile.photoUpload')}
              >
                <Camera className="h-5 w-5" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>

            <div className="space-y-2 text-center sm:text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" />
                {t('myProfile.subtitle')}
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{displayUser.name}</h1>
                <p className="mt-1 text-sm text-white/80">{displayUser.email}</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
                  {t('myProfile.heroRole')}: {getRoleLabel(displayUser.dealershipRole || displayUser.role?.name, t)}
                </span>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
                  {t('myProfile.heroStatus')}: {userStatusLabel}
                </span>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">
                  {contextLabel}
                </span>
              </div>
            </div>
          </div>

          <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-2">
            <Card className="border-white/15 bg-white/10 text-white backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] text-white/70">{t('myProfile.email')}</p>
                    <p className="text-sm font-semibold">{displayUser.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-white/15 bg-white/10 text-white backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] text-white/70">{t('myProfile.phone')}</p>
                    <p className="text-sm font-semibold">
                      {primaryPhone.phone ? formatPhone(primaryPhone.codeArea, primaryPhone.area, primaryPhone.phone) : '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-white/15 bg-white/10 text-white backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2">
                    <Clock3 className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] text-white/70">{t('myProfile.heroLastLogin')}</p>
                    <p className="text-sm font-semibold">{formatDate(displayUser.lastLogin)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-white/15 bg-white/10 text-white backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-white/15 p-2">
                    <BadgeCheck className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] text-white/70">{t('myProfile.heroCreatedAt')}</p>
                    <p className="text-sm font-semibold">{formatDate(displayUser.createdAt)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-emerald-700 dark:text-emerald-300">
          <ShieldCheck className="h-4 w-4" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Tabs defaultValue="data" className="w-full">
            <div className="border-b border-border px-4 pt-4 sm:px-6">
              <TabsList className="grid h-auto w-full grid-cols-3 bg-muted/60 p-1">
                <TabsTrigger value="data">{t('myProfile.tabs.data')}</TabsTrigger>
                <TabsTrigger value="photo">{t('myProfile.tabs.photo')}</TabsTrigger>
                <TabsTrigger value="security">{t('myProfile.tabs.security')}</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="data" className="mt-0 p-4 sm:p-6">
              <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">{t('myProfile.tabs.data')}</h2>
                    <p className="text-sm text-muted-foreground">{t('myProfile.subtitle')}</p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-foreground">{t('myProfile.name')}</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t('myProfile.namePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-foreground">{t('myProfile.email')}</label>
                      <Input
                        name="email"
                        type="email"
                        value={displayUser.email}
                        disabled
                        placeholder={t('myProfile.emailPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">{t('myProfile.phoneCountryCode')}</label>
                      <Input
                        name="codeArea"
                        value={formData.codeArea}
                        onChange={handleInputChange}
                        placeholder="+55"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">{t('myProfile.phoneArea')}</label>
                      <Input
                        name="area"
                        value={formData.area}
                        onChange={handleInputChange}
                        placeholder="11"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-foreground">{t('myProfile.phoneNumber')}</label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={t('myProfile.phonePlaceholder')}
                      />
                      <p className="text-xs text-muted-foreground">{t('myProfile.phoneHelp')}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 rounded-2xl border border-border bg-surface/60 p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.heroStatus')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
                      <div className="rounded-lg bg-accent/10 p-2 text-accent">
                        <Mail className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{t('myProfile.email')}</p>
                        <p className="text-muted-foreground">{displayUser.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
                      <div className="rounded-lg bg-accent/10 p-2 text-accent">
                        <BadgeCheck className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{t('myProfile.heroRole')}</p>
                        <p className="text-muted-foreground">{getRoleLabel(displayUser.dealershipRole || displayUser.role?.name, t)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
                      <div className="rounded-lg bg-accent/10 p-2 text-accent">
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{t('myProfile.heroStatus')}</p>
                        <p className="text-muted-foreground">{userStatusLabel}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="photo" className="mt-0 p-4 sm:p-6">
              <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('myProfile.photoTitle')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="flex flex-col items-center gap-4 text-center">
                      <div className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-full border-4 border-border bg-muted shadow-lg">
                        {displayAvatar ? (
                          <img src={displayAvatar} alt={displayUser.name} className="h-full w-full object-cover" />
                        ) : (
                          <User className="h-16 w-16 text-muted-foreground" />
                        )}
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-base font-semibold text-foreground">{displayUser.name}</h3>
                        <p className="text-sm text-muted-foreground">{t('myProfile.photoDescription')}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-center gap-3">
                      <Button type="button" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="mr-2 h-4 w-4" />
                        {t('myProfile.photoUpload')}
                      </Button>
                      <Button type="button" variant="outline" onClick={handleRemoveAvatar}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('myProfile.photoRemove')}
                      </Button>
                    </div>

                    <p className="text-center text-xs text-muted-foreground">{t('myProfile.photoHint')}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('myProfile.tabs.photo')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-2xl border border-border bg-surface/70 p-4">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-accent/10 p-2 text-accent">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{t('myProfile.subtitle')}</p>
                          <p className="text-sm text-muted-foreground">{t('myProfile.photoDescription')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-4">
                      <p className="text-sm font-medium text-foreground">{t('myProfile.heroCreatedAt')}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{formatDate(displayUser.createdAt)}</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-4">
                      <p className="text-sm font-medium text-foreground">{t('myProfile.heroLastLogin')}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{formatDate(displayUser.lastLogin)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="security" className="mt-0 p-4 sm:p-6">
              <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr]">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('myProfile.securityTitle')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{t('myProfile.securityDescription')}</p>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-2xl border border-border bg-surface/60 p-4">
                        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.heroStatus')}</p>
                        <p className="mt-1 text-base font-semibold text-foreground">{userStatusLabel}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {displayUser.isActive ? t('myProfile.securityActive') : t('myProfile.securityInactive')}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-border bg-surface/60 p-4">
                        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.heroRole')}</p>
                        <p className="mt-1 text-base font-semibold text-foreground">{getRoleLabel(displayUser.dealershipRole || displayUser.role?.name, t)}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{contextLabel}</p>
                      </div>
                      <div className="rounded-2xl border border-border bg-surface/60 p-4 md:col-span-2">
                        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.heroLastLogin')}</p>
                        <p className="mt-1 text-base font-semibold text-foreground">{formatDate(displayUser.lastLogin)}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{displayUser.lastLogin ? '' : t('myProfile.securityNeverLogin')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('myProfile.tabs.data')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-2xl border border-border bg-card p-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.email')}</p>
                      <p className="mt-1 text-sm font-medium text-foreground">{displayUser.email}</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.phone')}</p>
                      <p className="mt-1 text-sm font-medium text-foreground">
                        {primaryPhone.phone ? formatPhone(primaryPhone.codeArea, primaryPhone.area, primaryPhone.phone) : '—'}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-border bg-card p-4">
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{t('myProfile.heroCreatedAt')}</p>
                      <p className="mt-1 text-sm font-medium text-foreground">{formatDate(displayUser.createdAt)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => navigate(-1)}>
          {t('myProfile.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {isSaving ? t('myProfile.saving') : t('myProfile.save')}
        </Button>
      </div>
    </div>
  )
}
